// =============================================================================
// selectors/share.ts — the seam `ui/` reaches sharing through.
// -----------------------------------------------------------------------------
// Two jobs. The first is the layering pass-through: `ui/` may not import
// `domain/` at all, not even a type, so the allow-list, the expiry choices and
// the frame maths come through here.
//
// The second is composition, and it is the one with a decision in it: a shared
// report is built from FOUR things that live in four places — the rendered
// markup (io, because it is the DOM), the trades' shareable fields (io, because
// it is a read), the id→label registries (the app's state) and the rules about
// what may be published (domain). This module is where they meet, and it is the
// only place that knows the whole shape.
// =============================================================================

export {
  SHARE_EXPIRY_CHOICES, SHARE_EXPIRY_DEFAULT_INDEX,
  SHARE_FRAME_MAX_PX, SHARE_FRAME_MIN_PX,
  activeAccountLabel, isShareKind, maskAccountDigits, shareExpiresAt, shareFrameHeightPx,
  type ShareDisciplineFlag, type ShareExpiryChoice, type ShareKind,
  type ShareOptions, type SharePayload, type ShareTag, type ShareTrade,
} from '../domain/share';

export {
  SHARE_PARAM, SharedReportUnavailable, createShare, fetchSharedReport,
  loadShareLinks, revokeShare, shareLinkFor, shareTokenFrom,
  type FetchedShare, type ShareLinkRow, type SharedReportError,
} from '../io/share/shares';

export { copyShotsIntoPayload, newShareToken } from '../io/share/capture';
export { loadTradesForShare, type ShareTradeRow } from '../io/share/tradeRead';

import { effectiveTradeDate } from '../domain/session';
import {
  maskAccountDigits, shareTradeData,
  type ShareKind, type ShareOptions, type SharePayload, type ShareTrade,
} from '../domain/share';
import type { DayBoundaryMode } from '../domain/range';
import type { PnlUnit } from '../domain/types';
import { captureShareRegion } from '../io/share/capture';
import type { ShareTradeRow } from '../io/share/tradeRead';

/** The registries an id is resolved against, as the app already holds them. */
export interface ShareRegistrySources {
  patterns: readonly { id: string; name: string }[];
  exitModels: readonly { id: string; name: string }[];
  /** From `parseTagRegistry(prefs.customTags)`. */
  tags: readonly { id: string; name: string; color?: string | null }[];
  /** `TRADE_BOOLEANS`, in the order the trade form lays them out. */
  flags: readonly { key: string; label: string }[];
  /** Accounts, for the per-trade label. */
  accounts: readonly { id: string; name?: string | null; externalId?: string | null }[];
}

export interface BuildShareArgs {
  /** The element to capture. Beta captures the WHOLE tab, minus `[data-noshare]`. */
  region: Element;
  kind: ShareKind;
  title: string;
  subtitle: string;
  /** The active-account label for the header. Masked on the way through. */
  accountLabel: string;
  /**
   * ⚠ THE SAME SET THE REPORT LISTS. A wider one publishes trades the report
   * does not even mention — readable in the payload regardless of what is drawn.
   */
  rows: readonly ShareTradeRow[];
  mode: DayBoundaryMode;
  /** The sharer's display unit — the whole report is in it, table included. */
  unit: PnlUnit;
  /**
   * Each trade's value IN THAT UNIT, by id.
   *
   * ⚠ CONVERTED BY THE TAB, which owns the contract catalogue. Ticks need a
   * tick size and R needs a risk basis; a visitor has neither and never will, so
   * the conversion has to happen while the sharer is still on the page.
   */
  valueById: Readonly<Record<string, number | null>>;
  registries: ShareRegistrySources;
  opts: ShareOptions;
  /** Milliseconds. Passed in so the payload is reproducible in a test. */
  now: number;
}

/**
 * Freeze the report on screen into the row that will be stored.
 *
 * ⚠ SYNCHRONOUS AND SIDE-EFFECT FREE. Screenshots are copied in afterwards, by
 * the caller, because that is the only asynchronous part — and because a share
 * with `shots` off must not touch storage at all.
 */
export function buildSharePayload(a: BuildShareArgs): { payload: SharePayload; trades: ShareTrade[] } {
  const { html, contentHeight } = captureShareRegion(a.region);

  const nameById = new Map(
    a.registries.accounts.map((x) => [String(x.id), x.name || x.externalId || '(unnamed)']),
  );

  const trades = shareTradeData(
    a.rows.map((row) => ({
      trade: row,
      // ⚠ THE SESSION RULE, not `row.date`. A 22:36 trade belongs to the next
      // session, and the report above the table was built on that rule — a
      // payload that disagreed would list the trade under a different day than
      // the picture it sits beneath.
      effectiveDate: effectiveTradeDate(row, a.mode),
      // ⚠ NO `sourceAccount` FALLBACK, unlike the signed-in table's labeller.
      // That fallback renders the RAW broker string for an unassigned trade,
      // which is one of the four things that never travel — so an unassigned
      // trade is published with no account rather than with the export's own
      // identifier for it.
      accountLabel: row.accountId ? (nameById.get(String(row.accountId)) ?? '') : '',
      value: a.valueById[String(row.id)] ?? null,
    })),
    a.opts,
    a.registries,
  );

  return {
    payload: {
      unit: a.unit,
      title: a.title,
      subtitle: a.subtitle,
      // 🔴 MASKED HERE, AND IT WAS NOT. The line read `a.accountLabel` and the
      // comment on the field above it said "masked on the way through" — so the
      // rule was documented, believed, and not applied.
      //
      // The result is visible on any real share: the trades table renders
      // `7a***b44` (that path goes through `shareTradeData`, which masks) while
      // the HEADER, directly under the title, renders `7a735b44` in full. Josh
      // spotted it in a screenshot of his own report.
      //
      // ⚠ This is the one label a reader cannot miss — it is above the fold,
      // and filtering to a single account before sharing is the natural thing to
      // do, which is exactly what puts the account's own name there.
      accountLabel: maskAccountDigits(a.accountLabel),
      html,
      contentHeight,
      trades,
      notesIncluded: !!a.opts.notes,
      capturedAt: new Date(a.now).toISOString(),
    },
    trades,
  };
}
